import c from 'clsx';
import type { ComponentChildren } from 'preact';
import { useMouse } from '~/shared/hooks/use-mouse';
import { useMove } from '~/shared/hooks/use-move';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useMergedRef } from '~/shared/hooks/use-merged-ref';
import type { PlayerStore } from '@/player/store';
import { useViewportSize } from '@/shared/hooks/use-viewport-size';
import { shouldCommitSeek } from './seeker-logic';

function formatTime(time: number) {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time - minutes * 60);
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
}

type SeekerProps = {
  showScrubberKnob?: boolean;
  onSeek: (time: number) => void;
  store: PlayerStore;
  loading: boolean;
};

export default function Seeker({ onSeek, loading, store }: SeekerProps) {
  const { data } = store;
  const [seekerWidth, setSeekerWidth] = useState(0);
  const [value, setValue] = useState(0);
  const [waitingForSeek, setWaitingForSeek] = useState(false);
  const [pendingSeekTarget, setPendingSeekTarget] = useState<number | null>(null);
  const { ref, active } = useMove(({ x }) => setValue(x));
  const { ref: mref, x, active: hover } = useMouse();
  const { width: viewportWidth, height: viewportHeight } = useViewportSize();
  const previousActiveRef = useRef(active);
  const onSeekRef = useRef(onSeek);

  const refs = useMergedRef(ref, mref);

  useEffect(() => {
    if (ref.current) {
      setSeekerWidth(ref.current.offsetWidth);
    }
    // recalculate seeker width on viewport resize
  }, [viewportWidth, viewportHeight]);

  useEffect(() => {
    onSeekRef.current = onSeek;
  }, [onSeek]);

  useEffect(() => {
    const wasActive = previousActiveRef.current;
    previousActiveRef.current = active;

    if (shouldCommitSeek({ wasActive, active })) {
      const nextSeek = value * data.duration.value;
      setPendingSeekTarget(nextSeek);
      setWaitingForSeek(true);
      onSeekRef.current(nextSeek);
    }
  }, [value, active, data.duration.value]);

  useEffect(() => {
    if (!waitingForSeek || pendingSeekTarget === null) {
      return;
    }

    if (Math.abs(data.seek.value - pendingSeekTarget) <= 0.5) {
      setWaitingForSeek(false);
      setPendingSeekTarget(null);
    }
  }, [data.seek.value, waitingForSeek, pendingSeekTarget]);

  useEffect(() => {
    setValue(store.progress.value / 100);
    setWaitingForSeek(false);
    setPendingSeekTarget(null);
  }, [data.episode.value?.media]);

  useEffect(() => {
    if (!active && !waitingForSeek) {
      setValue(store.progress.value / 100);
    }
  }, [store.progress.value, active, waitingForSeek]);

  const dVal = active ? value * seekerWidth : x;
  const scrubbing = active && data.duration.value > 0;
  const scrubbingOrHovering = (hover || active) && scrubbing;

  return (
    <div
      ref={refs}
      style={{
        '-webkit-user-drag': 'none',
        '-webkit-tap-highlight-color': 'rgba(0,0,0,0)',
      }}
      class={c(
        'max-w-[75rem]  relative cursor-pointer py-2 -my-2 touch-none select-none',
        loading && 'animate-pulse',
        data.duration.value <= 0 && 'pointer-events-none'
      )}
    >
      <div class="bg-white w-full top-0 h-[6px] z-50">
        {scrubbingOrHovering ? (
          <div
            class="absolute -top-full -translate-x-1/2"
            style={{ left: `${(dVal / seekerWidth) * 100}%` }}
          >
            <TimePill>
              {formatTime((dVal / seekerWidth) * data.duration.value)}
            </TimePill>
          </div>
        ) : null}

        <div
          class="absolute h-[6px] bg-brand-600 cursor-pointer"
          style={{
            width: `${value * 100}%`,
          }}
        />
      </div>
    </div>
  );
}

export function TimePill({ children }: { children: ComponentChildren }) {
  return (
    <div
      class={c(
        'bg-brand-600  leading-none py-1 px-2 inline-flex items-center',
        'text-[0.5rem] text-white font-mono',
        'rounded-xl relative',
        'after:absolute after:left-1/2 after:-bottom-2',
        'after:-translate-x-1/2',
        'after:bg-transparent after:border-transparent',
        'after:border-solid after:border-r-4 after:border-b-4',
        'after:border-l-4 after:border-t-4 after:border-t-brand-600',
        'after:h-0 after:w-0'
      )}
    >
      <time>{children}</time>
    </div>
  );
}
